import React from 'react';
import { motion } from 'framer-motion';
export type ArchMode = 'anterior' | 'everyday' | 'posterior' | 'cervical' | 'shade' | 'all' | 'none';
interface DentalArchProps {
  mode?: ArchMode;
  accent: string;
  className?: string;
}
interface Tooth {
  x: number;
  y: number;
  rotation: number;
  width: number;
  height: number;
  zone: 'posterior' | 'everyday' | 'anterior';
}
const COUNT = 14;
const geometry = (index: number) => {
  const angle = Math.PI * (1 - index / (COUNT - 1));
  return {
    x: 200 + 158 * Math.cos(angle),
    y: 214 - 146 * Math.sin(angle),
    rotation: 90 - angle * 180 / Math.PI
  };
};
const teeth: Tooth[] = Array.from({
  length: COUNT
}, (_, index) => {
  const {
    x,
    y,
    rotation
  } = geometry(index);
  const mirrored = Math.min(index, COUNT - 1 - index);
  const zone: Tooth['zone'] = mirrored <= 1 ? 'posterior' : mirrored <= 3 ? 'everyday' : 'anterior';
  const width = zone === 'posterior' ? 26 : zone === 'everyday' ? 20 : mirrored === 6 ? 21 : 17;
  const height = zone === 'posterior' ? 30 : zone === 'everyday' ? 27 : mirrored === 5 ? 30 : 33;
  return {
    x,
    y,
    rotation,
    width,
    height,
    zone
  };
});
const isActive = (tooth: Tooth, index: number, mode: ArchMode) => {
  if (mode === 'all') return true;
  if (mode === 'none') return false;
  if (mode === 'shade') return tooth.zone === 'anterior';
  if (mode === 'cervical') return tooth.zone !== 'anterior';
  return tooth.zone === mode;
};

/**
 * The arch is feedback, not a form control: it lights the region of the mouth
 * the visitor is talking about while they choose from the list beside it.
 */
export function DentalArch({
  mode = 'none',
  accent,
  className = ''
}: DentalArchProps) {
  return <svg viewBox="0 0 400 260" className={className} role="img" aria-label="Dental arch diagram" data-id="element-418" data-mp="1ue4shkpv19xi">
      <path d="M42 214 C 42 90, 120 30, 200 30 C 280 30, 358 90, 358 214" fill="none" stroke="var(--enamel)" strokeOpacity="0.08" strokeWidth="1" data-id="element-419" data-mp="28dwcc2325kjf" />

      {mode === 'cervical' && <motion.path d="M60 212 C 62 116, 128 52, 200 50 C 272 52, 338 116, 340 212" fill="none" stroke={accent} strokeWidth="2" strokeDasharray="3 6" initial={{
      pathLength: 0,
      opacity: 0
    }} animate={{
      pathLength: 1,
      opacity: 0.85
    }} transition={{
      duration: 1.2,
      ease: 'easeInOut'
    }} data-id="element-420" data-mp="11381patnju7j" />}

      {teeth.map((tooth, index) => {
      const active = isActive(tooth, index, mode);
      return <motion.rect key={index} x={-tooth.width / 2} y={-tooth.height / 2} width={tooth.width} height={tooth.height} rx={tooth.zone === 'anterior' ? 5 : 8} transform={`translate(${tooth.x} ${tooth.y}) rotate(${tooth.rotation})`} stroke="var(--enamel)" strokeWidth="1" initial={false} animate={{
        fill: active ? accent : 'rgba(244,242,237,0.03)',
        fillOpacity: active ? mode === 'shade' ? 0.25 + index % 3 * 0.16 : 0.42 : 1,
        strokeOpacity: active ? 0.5 : 0.14
      }} transition={{
        duration: 0.55,
        ease: 'easeOut',
        delay: active ? index * 0.025 : 0
      }} data-id="element-421" data-mp="1oqg8jx6qbbbo" data-mpi={index} />;
    })}
    </svg>;
}